import BigNumber from 'bignumber.js';
import { PRICING_MODELS } from 'sonata-static-storage/lib/samples/pricingModels.samples';

import type {
  BidModelInterface,
  BidModelsMediaTypeEnum
} from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';

export const isPerMilPricingModel = (pricingModel: string): boolean =>
  Boolean(PRICING_MODELS.find(({ model }) => model === pricingModel)?.perMil);

/**
 * Raises every bid price to its minimum. When the pricing model switches between a per mille kind (CPM) and a
 * unitary kind (CPC, CPV...) the previous price is meaningless, so it is reset to the minimum instead.
 *
 * Minimums arrive in display units (per mille models x1000) while prices are stored unitary.
 * Returns `null` when no price needs to change.
 */
export function applyMinimumBidPrices(
  bidModels: BidModelInterface[],
  minimumBidPrices: Partial<Record<keyof typeof BidModelsMediaTypeEnum, number>> | undefined,
  previousPricingModel?: string
): BidModelInterface[] | null {
  let changed = false;
  const newBidModels = bidModels.map(bidModel => {
    const minimum = minimumBidPrices?.[bidModel.mediaType as keyof typeof BidModelsMediaTypeEnum];
    if (minimum === undefined) {
      return bidModel;
    }
    const { pricingModel, price } = bidModel.bid;
    const perMil = isPerMilPricingModel(pricingModel);
    const storedMinimum = new BigNumber(minimum).dividedBy(perMil ? 1000 : 1);
    const kindChanged = previousPricingModel !== undefined && isPerMilPricingModel(previousPricingModel) !== perMil;
    const newPrice = kindChanged ? storedMinimum : BigNumber.max(price, storedMinimum);
    if (newPrice.isEqualTo(price)) {
      return bidModel;
    }
    changed = true;
    return { ...bidModel, bid: { ...bidModel.bid, price: newPrice.toNumber() } };
  });
  return changed ? newBidModels : null;
}
